import { useQueryString } from "hooks/general-hooks";
import { CLASSES_AND_SPECS } from "lib/classes-and-specs";
import { capitalizeFirstLetter, titleCase } from "lib/general-helper";
import {
  convertDisplayNameToDataName,
  getFactionFromNum,
  getFormattedNames,
  getIsHealer,
  getIsTank,
  getPrimaryStatLabel,
} from "lib/wowHelpers";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as Arrow } from "svg/arrow-right-line.svg";
import MediaQuery from "components/MediaQuery";
import ProbuildsStatsSection from "../ProbuildsStatsSection";
import { validParams } from "query_parameters/valid_parameters";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import TalentTreeSection from "components/Pages/Builds/TalentTreeSection";
import ProbuildsTalentTreeHeader from "../ProbuildsTalentTreeHeader";
import { WoWContext } from "components/App";
import ProbuildsGearGuide from "../ProbuildsGearGuide";
import MythicPlusActivity from "../MythicPlusActivity";
import EchoOtherPlayers from "./EchoOtherPlayers";
import EncounterFilter from "./EncounterFilter";
import NoData from "components/Common/NoData";
import { API_WOW_ROOT_PATH } from "api/apiHelpers";
import { ReactComponent as DPSIcon } from "svg/wow-icon-dps.svg";
import { ReactComponent as TankIcon } from "svg/wow-icon-tank.svg";
import { ReactComponent as HealerIcon } from "svg/wow-icon-healer.svg";
import { DateTime } from "luxon";
import { ENCOUNTER_IDS } from "lib/encounter-ids";

const EchoProfile = () => {
  const { data: echoPersonalData, loading: personalDataIsLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/probuilds/echo-personal-data.json`,
    {
      ssr: true,
    },
  );

  const { search, pathname } = useLocation();
  const queryParams = useQueryString();
  const pathArr = pathname.split("/");
  const player = capitalizeFirstLetter(pathArr[3]);
  const playerPersonalData = echoPersonalData?.filter((data) => data.name === player)[0];
  const usingArmoryData = !queryParams["encounter"];
  const toonAccessor = usingArmoryData ? "toon" : "rwfToon";

  const { data: armoryData, loading: armoryDataIsLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/probuilds/${playerPersonalData?.[toonAccessor]?.toLowerCase()}.json`,
    {
      ssr: true,
    },
  );

  const { data: wclData, loading: wclDataIsLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/probuilds/WCL/${[ENCOUNTER_IDS.raid[queryParams["encounter"]]]}/${playerPersonalData?.rwfToon}.json`,
    {
      ssr: true,
    },
  );
  const playerData = usingArmoryData ? armoryData : wclData;
  const specType = convertDisplayNameToDataName(playerData?.spec);
  const classType = convertDisplayNameToDataName(playerData?.class) ?? "";
  const playerRole = getIsHealer(specType) ? "Healer" : getIsTank(specType) ? "Tank" : "DPS";
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const classColor = CLASSES_AND_SPECS.filter((data) => data.class === displayClassName)[0]?.color;
  const hasData = Object.keys(playerData ?? {}).length > 0;
  const selectedTalents = usingArmoryData ? playerData?.all_talents?.split("-") : playerData?.talents?.split("-");
  const faction = usingArmoryData ? playerData?.faction : getFactionFromNum(playerData?.faction);
  const toonName = usingArmoryData ? playerPersonalData?.toon : playerPersonalData?.rwfToon;

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [player]);

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`https://static.bigbrain.gg/wow/tree/v6/${classType}_${specs?.[i]?.value}.json`, {
      ssr: true,
    });
    allTrees[specs?.[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];

  let highestPrimaryStat = "";
  let highestPrimaryValue = 0;
  if (usingArmoryData) {
    for (let stat in armoryData?.primary_stats) {
      if (stat !== "stamina" && armoryData.primary_stats[stat].effective > highestPrimaryValue) {
        highestPrimaryStat = stat;
        highestPrimaryValue = armoryData.primary_stats[stat].effective;
      }
    }
  } else {
    highestPrimaryStat = getPrimaryStatLabel(playerData?.primary_stats ?? {});
    highestPrimaryValue = playerData?.primary_stats?.[highestPrimaryStat] ?? 0;
  }

  const statSectionData = {
    pageTitle: "Race to World First",
    lvl1Stat: capitalizeFirstLetter(toonName) + ", Tarren Mill",
    lvl2Stat1: playerRole === "Healer" ? <HealerIcon /> : playerRole === "Tank" ? <TankIcon /> : <DPSIcon />,
    lvl2Stat1Label: playerRole,
    lvl2Stat2Label: "Mythic Raid",
    lvl2Stat2: `${armoryData?.completed_bosses ?? 0}/${armoryData?.total_bosses ?? 0}`,
    lvl2Stat2Sub: armoryData?.completed_date ? DateTime.fromMillis(armoryData?.completed_date).toFormat("M/d/yyyy") : "",
    lvl2Stat3: playerData?.item_level,
    lvl2Stat4Label: "M+ Rating",
    lvl2Stat4: Math.trunc(armoryData?.rating ?? 0)?.toLocaleString("en-US"),
    primaryStatLabel: capitalizeFirstLetter(highestPrimaryStat),
    primaryStatValue: highestPrimaryValue.toLocaleString("en-US"),
    stamina: usingArmoryData
      ? armoryData?.primary_stats?.stamina?.effective?.toLocaleString("en-US")
      : wclData?.primary_stats?.stamina?.toLocaleString("en-US"),
    crit: usingArmoryData
      ? `${armoryData?.secondary_stats?.crit?.toFixed(1)}%`
      : wclData?.secondary_stats?.crit.toLocaleString("en-US"),
    haste: usingArmoryData
      ? `${armoryData?.secondary_stats?.haste?.toFixed(1)}%`
      : wclData?.secondary_stats?.haste.toLocaleString("en-US"),
    versatility: usingArmoryData
      ? `${armoryData?.secondary_stats?.versatility_damage_done?.toFixed(1)}%`
      : wclData?.secondary_stats?.versatility.toLocaleString("en-US"),
    mastery: usingArmoryData
      ? `${armoryData?.secondary_stats?.mastery?.toFixed(1)}%`
      : wclData?.secondary_stats?.mastery.toLocaleString("en-US"),
  };

  const timestamp = usingArmoryData ? armoryData?.timestamp : wclData?.timestamp;

  const probuildsContextValue = {
    allTrees,
    selectedTalents,
    specType,
  };

  if (personalDataIsLoading || armoryDataIsLoading || wclDataIsLoading) return null;

  return (
    <WoWContext.Provider value={probuildsContextValue}>
      <div className="wow-layout-container">
        <div className="echo-background">
          <div className="image-wrap" />
          <img src="https://static.bigbrain.gg/wow/assets/echo/scale_background.png" className="background-image" />
          <div className="wow-content probuilds-container echo-profile-container">
            <Helmet>
              <title>{player}: Race to World First Build</title>
              <meta name="description" content={`Follow ${player} in the Race to World First`} />
            </Helmet>
            <Link
              to={{
                pathname: `/wow/echo`,
                search,
              }}
            >
              <div className="roster-back-button">
                <Arrow /> Back to Echo Roster
              </div>
            </Link>
            <div className="echo-profile-header-container">
              <div className="image-square" style={{ border: `1px solid ${classColor}` }}>
                <img src={playerPersonalData?.img} alt={playerPersonalData?.name} className="profile-img" />
              </div>
              <div className="header-text-container">
                <div className="header-name-and-details-container">
                  <div className="player-name">{playerPersonalData?.name}</div>
                  {hasData && (
                    <div className="details-container">
                      <div className="class-spec-icon-and-text">
                        <img
                          src={`https://static.bigbrain.gg/wow/spec-icons/${dataClassName}-${dataSpecName}.jpg`}
                          alt={`${displaySpecName} ${displayClassName}`}
                          className="spec-class-icon"
                        />{" "}
                        {displaySpecName} {displayClassName}
                      </div>
                      <div className="faction-icon-and-text">
                        <img
                          src={`https://static.bigbrain.gg/wow/assets/wow-${faction}.svg`}
                          alt={faction}
                          className="faction-icon"
                        />{" "}
                        {titleCase(armoryData?.race)}
                      </div>
                    </div>
                  )}
                </div>
                <MediaQuery min="TABLET" max="DESKTOP_LARGE" RenderNullOnFail>
                  <div className="header-bio-container">{playerPersonalData?.bio}</div>
                </MediaQuery>
              </div>
            </div>
            <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE" RenderNullOnFail>
              <div className="header-bio-container">{playerPersonalData?.bio}</div>
            </MediaQuery>
            {hasData ? (
              <>
                <div className="encounter-filter-container">
                  <div className="roster-encounter-filter">
                    <EncounterFilter />
                  </div>
                </div>
                <ProbuildsStatsSection
                  characterData={statSectionData}
                  timestamp={timestamp}
                  dataSource={usingArmoryData ? "Blizzard Armory" : "Warcraft Logs"}
                />
                <TalentTreeSection
                  classAndSpecTree={classAndSpecTree}
                  className="probuilds-talent-tree"
                  classType={classType}
                  hideHeader
                  selectedTalents={selectedTalents}
                  specType={specType}
                  treeHeaderComponent={ProbuildsTalentTreeHeader}
                />
                <ProbuildsGearGuide builds={playerData.items} />
                <MythicPlusActivity keys={armoryData?.keys ?? []} />
                <EchoOtherPlayers
                  personalData={echoPersonalData}
                  wclData={wclData?.[queryParams["encounter"]]}
                  currentPlayer={playerPersonalData?.name}
                  showTitle
                />
              </>
            ) : (
              <div>
                <div className="encounter-filter-container">
                  <div className="roster-encounter-filter">
                    <EncounterFilter />
                  </div>
                </div>
                <NoData />
              </div>
            )}
          </div>
        </div>
      </div>
    </WoWContext.Provider>
  );
};

export default EchoProfile;
